import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  Alert,
  CircularProgress,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Divider,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Add, Edit, Delete, Search, Visibility, OpenInNew } from '@mui/icons-material';
import { agreementsApi } from '../services/agreements';
import { companiesApi } from '../services/companies';
import { servicesApi } from '../services/services';
import { AgreementList, Agreement, AgreementForm, AgreementServiceRelationForm, CompanyList, Service } from '../types';
import { useAuth } from '../hooks/useAuth';
import { format, parse } from 'date-fns';

const AgreementsPage: React.FC = () => {
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  const [agreements, setAgreements] = useState<AgreementList[]>([]);
  const [allAgreements, setAllAgreements] = useState<AgreementList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyActive, setShowOnlyActive] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [selectedAgreement, setSelectedAgreement] = useState<Agreement | null>(null);
  const [companies, setCompanies] = useState<CompanyList[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [formLoading, setFormLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState<AgreementForm>({
    company_id: 0,
    url: '',
    number: '',
    start_date: '',
    end_date: '',
    active: true,
    field1: '',
    field2: '',
    field3: '',
    field4: '',
    field5: '',
    service_relations: []
  });

  const loadAgreements = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await agreementsApi.getAgreements();
      setAllAgreements(data);
    } catch (err: any) {
      console.error('Error loading agreements:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Error loading agreements';
      setError(errorMessage);
      // If 404 or empty response, show empty list
      if (err.response?.status === 404 || err.response?.status === 200) {
        setAllAgreements([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const filterAgreements = (data: AgreementList[], search: string, onlyActive: boolean) => {
    let filtered = [...data];
    
    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(agreement => 
        agreement.number?.toLowerCase().includes(searchLower) ||
        agreement.company_name?.toLowerCase().includes(searchLower)
      );
    }
    
    // Active filter - учитываем и поле active, и даты
    if (onlyActive) {
      filtered = filtered.filter(agreement => {
        const dateActive = isActive(agreement.start_date, agreement.end_date);
        const agreementActive = agreement.active !== undefined ? agreement.active : true;
        return dateActive && agreementActive;
      });
    }
    
    setAgreements(filtered);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      filterAgreements(allAgreements, searchTerm, showOnlyActive);
    }
  };

  useEffect(() => {
    filterAgreements(allAgreements, searchTerm, showOnlyActive);
  }, [allAgreements, searchTerm, showOnlyActive]);

  const loadCompanies = async () => {
    try {
      const data = await companiesApi.getCompanies();
      setCompanies(data);
    } catch (err: any) {
      console.error('Error loading companies:', err);
    }
  };

  const loadServices = async () => {
    try {
      const data = await servicesApi.getServices(true);
      setServices(data);
    } catch (err: any) {
      console.error('Error loading services:', err);
    }
  };

  const loadAgreementDetails = async (id: number) => {
    try {
      setFormLoading(true);
      const agreement = await agreementsApi.getAgreement(id);
      setSelectedAgreement(agreement);
      return agreement;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error loading agreement data');
      return null;
    } finally {
      setFormLoading(false);
    }
  };

  useEffect(() => {
    loadAgreements();
    loadCompanies();
    loadServices();
  }, []);


  const handleOpenAgreement = async (agreementId: number) => {
    setError('');
    const agreement = await loadAgreementDetails(agreementId);
    if (agreement) {
      const canEdit = !isClient;
      setIsViewMode(!canEdit);
      setFormData({
        company_id: agreement.company_id,
        url: agreement.url || '',
        number: agreement.number,
        start_date: format(new Date(agreement.start_date), 'yyyy-MM-dd'),
        end_date: agreement.end_date ? format(new Date(agreement.end_date), 'yyyy-MM-dd') : '',
        active: agreement.active !== undefined ? agreement.active : true,
        field1: agreement.field1 || '',
        field2: agreement.field2 || '',
        field3: agreement.field3 || '',
        field4: agreement.field4 || '',
        field5: agreement.field5 || '',
        service_relations: agreement.service_relations ? agreement.service_relations.map(rel => {
          const price = typeof rel.price === 'number' ? rel.price : parseFloat(String(rel.price || 0));
          return {
            id: rel.id,
            service_id: rel.service_id,
            price,
            initialPrice: rel.id ? price : undefined,
            rel_type: rel.rel_type,
            active: rel.active !== undefined ? rel.active : true,
            field1: rel.field1 || '',
            field2: rel.field2 || '',
            field3: rel.field3 || '',
            field4: rel.field4 || '',
            field5: rel.field5 || ''
          };
        }) : []
      });
      setOpenEditDialog(true);
    }
  };

  const handleAddAgreement = () => {
    setError('');
    setSelectedAgreement(null);
    setFormData({
      company_id: 0,
      url: '',
      number: '',
      start_date: format(new Date(), 'yyyy-MM-dd'),
      end_date: '',
      active: true,
      field1: '',
      field2: '',
      field3: '',
      field4: '',
      field5: '',
      service_relations: []
    });
    setOpenEditDialog(true);
  };

  const handleAddServiceRelation = () => {
    setFormData({
      ...formData,
      service_relations: [...formData.service_relations, { service_id: 0, price: 0, rel_type: '1' }]
    });
  };

  const handleRemoveServiceRelation = async (index: number) => {
    const relation = formData.service_relations[index];
    
    // Если это существующий договор и у связи есть ID, удаляем через API
    if (selectedAgreement && relation.id) {
      try {
        setFormLoading(true);
        await agreementsApi.deleteAgreementServiceRelation(relation.id);
        // Перезагружаем данные договора
        await handleOpenAgreement(selectedAgreement.id);
      } catch (err: any) {
        const errorMessage = err.response?.data?.detail || err.message || 'Error deleting service relation';
        setError(errorMessage);
      } finally {
        setFormLoading(false);
      }
    } else {
      // Для новых договоров просто удаляем из формы
      setFormData({
        ...formData,
        service_relations: formData.service_relations.filter((_, i) => i !== index)
      });
    }
  };

  const handleServiceRelationChange = (index: number, field: keyof AgreementServiceRelationForm, value: any) => {
    if (field === 'service_id' && (value === '' || value === undefined)) value = 0;
    else if (field === 'service_id') value = Number(value);
    const newRelations = [...formData.service_relations];
    newRelations[index] = { ...newRelations[index], [field]: value };
    
    // Если изменилась услуга, можно обновить цену из услуги
    if (field === 'service_id' && value) {
      const service = services.find(s => s.id === value);
      if (service && service.price) {
        newRelations[index].price = service.price;
      }
    }
    
    // Валидация: цена 0 только у prepayment (проверка на уменьшение — при сохранении)
    if (field === 'price') {
      const priceValue = typeof value === 'number' ? value : parseFloat(String(value || 0));
      newRelations[index].price = isNaN(priceValue) ? 0 : priceValue;
      if (newRelations[index].price === 0) {
        const serviceId = newRelations[index].service_id;
        if (serviceId) {
          const service = services.find(s => s.id === serviceId);
          if (service && service.identifier !== 'prepayment') {
            setError('Price cannot be 0 for services other than Prepayment');
            return;
          }
        }
      }
      setError('');
    }
    
    setFormData({ ...formData, service_relations: newRelations });
  };

  const handleSaveAgreement = async () => {
    try {
      setFormLoading(true);
      setError('');
      
      if (!formData.company_id) {
        setError('Please select a company');
        return;
      }
      
      if (!formData.start_date) {
        setError('Please enter start date');
        return;
      }
      
      // Проверяем, что хотя бы одна услуга выбрана и заполнена
      const validServices = formData.service_relations.filter(
        rel => {
          const hasService = rel.service_id && rel.service_id > 0;
          if (!hasService) return false;
          
          // Проверяем цену
          if (rel.price === null || rel.price === undefined) return false;
          const priceValue = typeof rel.price === 'number' ? rel.price : parseFloat(String(rel.price || 0));
          const hasPrice = !isNaN(priceValue) && priceValue >= 0;
          
          // Валидация: цена 0 может быть только у prepayment
          if (priceValue === 0) {
            const service = services.find(s => s.id === rel.service_id);
            if (!service || service.identifier !== 'prepayment') {
              return false;
            }
          }
          
          return hasPrice;
        }
      );
      
      if (validServices.length === 0) {
        setError('At least one service must be selected and have a price specified');
        return;
      }
      
      // Дополнительная проверка: если есть услуги с ценой 0, они должны быть prepayment
      for (const rel of formData.service_relations) {
        if (rel.service_id && rel.service_id > 0) {
          const priceValue = typeof rel.price === 'number' ? rel.price : parseFloat(String(rel.price || 0));
          if (priceValue === 0) {
            const service = services.find(s => s.id === rel.service_id);
            if (!service || service.identifier !== 'prepayment') {
              setError('Price cannot be 0 for services other than Prepayment');
              return;
            }
          }
        }
      }
      
      // Для существующего договора: цену по услуге нельзя уменьшать
      if (selectedAgreement !== null) {
        for (const rel of formData.service_relations) {
          if (rel.id != null && rel.initialPrice != null) {
            const priceVal = typeof rel.price === 'number' ? rel.price : parseFloat(String(rel.price ?? 0));
            if (!isNaN(priceVal) && priceVal < rel.initialPrice) {
              const serviceName = services.find(s => s.id === rel.service_id)?.name || 'Service';
              setError(`Price cannot be decreased. ${serviceName}: minimum is $${rel.initialPrice}.`);
              return;
            }
          }
        }
      }
      
      const toApiDate = (s: string): string | null => {
        if (!s || /^\d{4}-\d{2}-\d{2}$/.test(s)) return s || null;
        try {
          let parsed = parse(s, 'MM/dd/yyyy', new Date());
          if (isNaN(parsed.getTime())) parsed = parse(s, 'M/d/yyyy', new Date());
          return !isNaN(parsed.getTime()) ? format(parsed, 'yyyy-MM-dd') : null;
        } catch {
          return null;
        }
      };
      const startDateApi = toApiDate(formData.start_date || '');
      if (!startDateApi) {
        setError('Please enter a valid start date (MM/DD/YYYY)');
        return;
      }
      const endDateApi = formData.end_date ? toApiDate(formData.end_date) : null;
      if (formData.end_date && endDateApi === null) {
        setError('Please enter a valid end date (MM/DD/YYYY) or leave empty');
        return;
      }
      const agreementData: any = {
        company_id: formData.company_id,
        url: formData.url || '',
        number: (formData.number && formData.number.trim()) || undefined,  // Автоматически генерируется на бэкенде, если не указан
        start_date: startDateApi,
        end_date: formData.end_date === '' ? null : (endDateApi || undefined),
        active: formData.active,
        field1: formData.field1 || undefined,
        field2: formData.field2 || undefined,
        field3: formData.field3 || undefined,
        field4: formData.field4 || undefined,
        field5: formData.field5 || undefined,
        service_relations: validServices.map(rel => ({
          service_id: rel.service_id,
          price: typeof rel.price === 'number' ? rel.price : parseFloat(String(rel.price || 0)),
          rel_type: rel.rel_type,
          field1: rel.field1 || undefined,
          field2: rel.field2 || undefined,
          field3: rel.field3 || undefined,
          field4: rel.field4 || undefined,
          field5: rel.field5 || undefined
        }))
      };
      
      if (selectedAgreement) {
        // Update existing agreement - обновляем только основные поля
        // Связи с услугами не трогаем - их можно только удалять и добавлять отдельно
        const updateData: any = {
          url: agreementData.url,
          number: agreementData.number,
          start_date: agreementData.start_date,
          end_date: agreementData.end_date,
          active: agreementData.active,
          field1: agreementData.field1,
          field2: agreementData.field2,
          field3: agreementData.field3,
          field4: agreementData.field4,
          field5: agreementData.field5
        };
        await agreementsApi.updateAgreement(selectedAgreement.id, updateData);
        // Применяем изменения active и цены по связям (цену можно только повышать)
        for (const rel of formData.service_relations) {
          if (!rel.id) continue;
          const payload: { active?: boolean; price?: number } = { active: rel.active !== false };
          if (rel.initialPrice != null) payload.price = typeof rel.price === 'number' ? rel.price : parseFloat(String(rel.price || 0));
          await agreementsApi.updateAgreementServiceRelation(rel.id, payload);
        }
        // Создаём новые связи (добавленные услуги)
        for (const rel of formData.service_relations) {
          if (rel.id) continue;
          if (!rel.service_id || rel.service_id <= 0) continue;
          const priceVal = typeof rel.price === 'number' ? rel.price : parseFloat(String(rel.price || 0));
          if (isNaN(priceVal) || priceVal < 0) continue;
          const svc = services.find(s => s.id === rel.service_id);
          if (svc?.identifier === 'prepayment' && priceVal === 0) { /* skip auto prepayment */ continue; }
          await agreementsApi.createAgreementServiceRelation({
            agreement_id: selectedAgreement.id,
            service_id: rel.service_id,
            price: priceVal,
            rel_type: rel.rel_type || '1'
          });
        }
      } else {
        // Create new agreement
        await agreementsApi.createAgreement(agreementData);
      }
      
      setError('');
      setOpenEditDialog(false);
      setIsViewMode(false);
      await loadAgreements();
      // selectedAgreement сбрасываем в onExited, чтобы при анимации закрытия не мелькал текст "Agreement number will be automatically..."
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error saving agreement');
    } finally {
      setFormLoading(false);
    }
  };


  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'MM/dd/yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'MM/dd/yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  const getRelTypeLabel = (relType: string): string => {
    const labels: Record<string, string> = {
      '1': 'Per Year',
      '2': 'Per Month',
      '3': 'One Time',
      '4': 'Per 3 Months',
      '5': 'Per 6 Months'
    };
    return labels[relType] || relType;
  };

  const isActive = (startDate: string, endDate?: string | null): boolean => {
    if (!endDate) return true; // Unlimited agreement
    const today = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    return today >= start && today <= end;
  };

  const formatEndDateInput = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return digits.slice(0, 2) + '/' + digits.slice(2);
    return digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4);
  };

  if (loading && agreements.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Agreements
        </Typography>
        {!isClient && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddAgreement}
          >
            Add Agreement
          </Button>
        )}
      </Box>

      <Box display="flex" gap={2} mb={3} alignItems="center">
        <TextField
          placeholder="Search by agreement number or company..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            filterAgreements(allAgreements, e.target.value, showOnlyActive);
          }}
          onKeyPress={handleSearchKeyPress}
          size="small"
          sx={{ flexGrow: 1 }}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
          }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={showOnlyActive}
              onChange={(e) => {
                setShowOnlyActive(e.target.checked);
                filterAgreements(allAgreements, searchTerm, e.target.checked);
              }}
            />
          }
          label="Show only active"
        />
        {searchTerm && (
          <Button
            variant="text"
            size="small"
            onClick={() => {
              setSearchTerm('');
              filterAgreements(allAgreements, '', showOnlyActive);
            }}
          >
            Reset
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Number</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {agreements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No agreements found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              agreements.map((agreement) => {
                // Учитываем и поле active, и даты начала/окончания
                const dateActive = isActive(agreement.start_date, agreement.end_date);
                const agreementActive = agreement.active !== undefined ? agreement.active : true;
                const active = dateActive && agreementActive;
                return (
                  <TableRow key={agreement.id}>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        fontWeight="medium"
                        sx={{ 
                          cursor: 'pointer',
                          color: 'primary.main',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => handleOpenAgreement(agreement.id)}
                      >
                        {agreement.number}
                      </Typography>
                    </TableCell>
                    <TableCell>{agreement.company_name}</TableCell>
                    <TableCell>{formatDate(agreement.start_date)}</TableCell>
                    <TableCell>
                      {agreement.end_date ? formatDate(agreement.end_date) : 'Unlimited'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={active ? 'Active' : 'Inactive'}
                        color={active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit/Create/View Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => {
          setError('');
          setOpenEditDialog(false);
          if (!isClient) {
            setIsViewMode(false);
          }
        }}
        TransitionProps={{
          onExited: () => setSelectedAgreement(null),
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {selectedAgreement ? (isViewMode ? 'View Agreement' : 'Edit Agreement') : 'Add Agreement'}
        </DialogTitle>
        <DialogContent>
          {formLoading && !selectedAgreement ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Company</InputLabel>
                    <Select
                      value={formData.company_id || ''}
                      onChange={(e) => setFormData({ ...formData, company_id: e.target.value as number })}
                      label="Company"
                      disabled={isClient || isViewMode}
                    >
                      <MenuItem value="">Not selected</MenuItem>
                      {companies.map((company) => (
                        <MenuItem key={company.id} value={company.id}>
                          {company.name} ({company.country})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {selectedAgreement && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Agreement Number"
                      value={formData.number || ''}
                      onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                      disabled={isClient || isViewMode}
                      helperText="Number is auto-generated when creating new agreement"
                    />
                  </Grid>
                )}
                {!selectedAgreement && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Agreement number will be automatically generated based on the start date (format: DDMMYY-number)
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="text"
                    placeholder="MM/DD/YYYY"
                    value={/^\d{4}-\d{2}-\d{2}$/.test(formData.start_date || '')
                      ? format(new Date(formData.start_date + 'T12:00:00'), 'MM/dd/yyyy')
                      : (formData.start_date || '')}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) {
                        setFormData({ ...formData, start_date: '' });
                        return;
                      }
                      try {
                        let parsed = parse(v, 'MM/dd/yyyy', new Date());
                        if (isNaN(parsed.getTime())) parsed = parse(v, 'M/d/yyyy', new Date());
                        setFormData({
                          ...formData,
                          start_date: !isNaN(parsed.getTime()) ? format(parsed, 'yyyy-MM-dd') : v
                        });
                      } catch {
                        setFormData({ ...formData, start_date: v });
                      }
                    }}
                    helperText={selectedAgreement ? undefined : 'Format: MM/DD/YYYY'}
                    required
                    disabled={isClient || isViewMode || selectedAgreement !== null}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="text"
                    placeholder="MM/DD/YYYY"
                    value={/^\d{4}-\d{2}-\d{2}$/.test(formData.end_date || '')
                      ? format(new Date(formData.end_date + 'T12:00:00'), 'MM/dd/yyyy')
                      : (formData.end_date || '')}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) {
                        setFormData({ ...formData, end_date: '' });
                        return;
                      }
                      const formatted = formatEndDateInput(v);
                      if (formatted.length === 10) {
                        try {
                          const parsed = parse(formatted, 'MM/dd/yyyy', new Date());
                          setFormData({
                            ...formData,
                            end_date: !isNaN(parsed.getTime()) ? format(parsed, 'yyyy-MM-dd') : formatted
                          });
                        } catch {
                          setFormData({ ...formData, end_date: formatted });
                        }
                      } else {
                        setFormData({ ...formData, end_date: formatted });
                      }
                    }}
                    helperText="Leave empty for unlimited agreement. Slashes are added automatically (MM/DD/YYYY)."
                    disabled={isClient || isViewMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.active && isActive(formData.start_date, formData.end_date || '')}
                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                        disabled={isClient || isViewMode}
                      />
                    }
                    label="Active"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Document URL"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder="https://...&#10;https://... (additional agreements)"
                    disabled={isClient || isViewMode}
                    multiline
                    rows={4}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1">
                      <strong>Agreement Services</strong>
                    </Typography>
                    {!isClient && !isViewMode && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={handleAddServiceRelation}
                    >
                      Add Service
                    </Button>
                    )}
                  </Box>
                  {formData.service_relations.length === 0 ? (
                    <Alert severity="info">Services are optional but can be added</Alert>
                  ) : (() => {
                    const displayRelations = formData.service_relations
                      .map((rel, index) => ({ rel, index }))
                      .filter(({ rel }) => services.find(s => s.id === rel.service_id)?.identifier !== 'prepayment');
                    if (displayRelations.length === 0) {
                      return <Alert severity="info">No other services in this agreement.</Alert>;
                    }
                    return (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Service</TableCell>
                            <TableCell align="right" width={150}>Price</TableCell>
                            <TableCell align="right" width={150}>Payment Type</TableCell>
                            {!isClient && selectedAgreement !== null && (
                              <TableCell align="center" width={80}>Active</TableCell>
                            )}
                            <TableCell width={50}></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {displayRelations.map(({ rel, index }) => {
                            const isExistingAgreement = selectedAgreement !== null;
                            const isExistingRow = Boolean(rel.id);
                            const isRowReadOnly = isExistingAgreement && isExistingRow;
                            const isServiceEditable = !isClient && !isViewMode && !isExistingAgreement;
                            const canToggleActive = !isClient && !isViewMode && isExistingAgreement && rel.id;
                            const isActive = rel.active !== undefined ? rel.active : true;
                            
                            return (
                            <TableRow key={index}>
                              <TableCell>
                                {isRowReadOnly ? (
                                  <Typography variant="body2">
                                    {services.find(s => s.id === rel.service_id)?.name || `Service ${rel.service_id}`}
                                  </Typography>
                                ) : (
                                  <FormControl fullWidth size="small">
                                    <Select
                                      value={rel.service_id || ''}
                                      onChange={(e) => handleServiceRelationChange(index, 'service_id', e.target.value)}
                                      disabled={isClient || isViewMode}
                                    >
                                      <MenuItem value="">Select service</MenuItem>
                                      {services.filter(s => s.identifier !== 'prepayment').map((service) => (
                                        <MenuItem key={service.id} value={service.id}>
                                          {service.name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                )}
                              </TableCell>
                              <TableCell>
                                {(isRowReadOnly && (isClient || isViewMode)) ? (
                                  <Typography variant="body2" align="right">
                                    ${typeof rel.price === 'number' ? rel.price.toFixed(2) : rel.price}
                                  </Typography>
                                ) : (
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={rel.price}
                                    onChange={(e) => handleServiceRelationChange(index, 'price', parseFloat(e.target.value) || 0)}
                                    inputProps={{ min: 0, step: '0.01' }}
                                    fullWidth
                                    disabled={isClient || isViewMode}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                {isRowReadOnly ? (
                                  <Typography variant="body2">
                                    {getRelTypeLabel(rel.rel_type)}
                                  </Typography>
                                ) : (
                                  <FormControl fullWidth size="small">
                                    <Select
                                      value={rel.rel_type}
                                      onChange={(e) => handleServiceRelationChange(index, 'rel_type', e.target.value)}
                                      disabled={isClient || isViewMode}
                                    >
                                      <MenuItem value="1">Per Year</MenuItem>
                                      <MenuItem value="2">Per Month</MenuItem>
                                      <MenuItem value="3">One Time</MenuItem>
                                      <MenuItem value="4">Per 3 Months</MenuItem>
                                      <MenuItem value="5">Per 6 Months</MenuItem>
                                    </Select>
                                  </FormControl>
                                )}
                              </TableCell>
                              {!isClient && selectedAgreement !== null && (
                                <TableCell align="center">
                                  {canToggleActive ? (
                                    <Switch
                                      size="small"
                                      checked={isActive}
                                      onChange={(e) => handleServiceRelationChange(index, 'active', e.target.checked)}
                                      color="primary"
                                    />
                                  ) : (
                                    <Chip label={isActive ? 'Yes' : 'No'} size="small" color={isActive ? 'success' : 'default'} />
                                  )}
                                </TableCell>
                              )}
                              <TableCell>
                                {!isClient && !isViewMode && (!isExistingAgreement || !rel.id) && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveServiceRelation(index)}
                                  color="error"
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                                )}
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    );
                  })()}
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenEditDialog(false);
            // Не сбрасываем isViewMode для клиентов, чтобы избежать кратковременного отображения редактируемых полей и кнопки Save
            if (!isClient) {
              setIsViewMode(false);
            }
          }} disabled={formLoading}>
            {isViewMode || isClient ? 'Close' : 'Cancel'}
          </Button>
          {!isClient && !isViewMode && (
          <Button
            onClick={handleSaveAgreement}
            variant="contained"
            disabled={formLoading || !formData.company_id || !formData.start_date}
          >
              {formLoading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AgreementsPage;
